const slug =
	<TFn extends (...args: any[]) => string>(func: TFn) =>
	(...args: Parameters<TFn>) => {
		const modifiedArgs = args
			.filter((arg) => arg !== undefined)
			.map((arg) => encodeURIComponent(arg));
		return func(...modifiedArgs);
	};

export const pret = (val: string) =>
	val
		.toLowerCase()
		.replace(/%20|\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-|-$/g, "");

export const routes = {
	contact: "/contact",
	franchise: "/franchise",
	thumbnail: {
		map: "/internal/thumbnail/map/[map-id]",
		dataset: "/internal/thumbnail/dataset/[dataset-id]",
	},
	accountName: {
		maps: {
			root: "/[account-name]/maps",
			mapId: {
				root: "/[account-name]/maps/[map-id]",
				tools: "/[account-name]/maps/[map-id]/tools",
				settings: {
					root: "/[account-name]/maps/[map-id]/settings",
				},
				mapName: {
					root: "/[account-name]/maps/[map-id]/[map-name]",
				},
			},
		},
		data: {
			root: "/[account-name]/data",
			datasetId: {
				root: "/[account-name]/data/[dataset-id]",
				settings: "/[account-name]/data/[dataset-id]/settings",
				datasetName: {
					root: "/[account-name]/data/[dataset-id]/[dataset-name]",
				},
			},
		},
		users: {
			root: "/[account-name]/users",
		},
		groups: {
			root: "/[account-name]/groups",
		},
		branding: {
			root: "/[account-name]/branding",
			localisation: "/[account-name]/branding/localisation",
			domainAndWhiteLabel: "/[account-name]/branding/domain-and-white-label",
			portal: "/[account-name]/branding/portal",
			theme: "/[account-name]/branding/theme",
			analytics: "/[account-name]/branding/analytics",
		},
		support: {
			root: "/[account-name]/support",
		},
		settings: {
			root: "/[account-name]/settings",
			profile: "/[account-name]/settings/profile",
			password: "/[account-name]/settings/password",
			checkout: "/[account-name]/settings/checkout",
			cancelSubscription: "/[account-name]/settings/cancel-subscription",
			invoice: "/[account-name]/settings/invoice",
			editSubscription: "/[account-name]/settings/edit-subscription",
			managedAccountRecovery:
				"/[account-name]/settings/managed-account-recovery",
			subscriptionConfirmation:
				"/[account-name]/settings/subscription-confirmation",
		},
	},
} as const;

export const paths = {
	unauthorized: "/401",
	notFound: "/404",
	sererError: "/500",
	pricing: "/pricing",
	partners: "/partners",
	changelog: "/changelog",
	solutionsGovernment: "/solutions/government",
	solutionsEnvironment: "/solutions/environment",
	home: "/",
	privacy: "/privacy",
	terms: "/terms",
	signUp: "/sign-up",
	signUpVerificationV3: "/sign-up-verification",
	signUpVerificationV4: slug(
		(email) => `/sign-up-verification?generation=v4&email=${email}`,
	),
	forgotPassword: "/forgot-password",
	updatePassword: slug((email) => `/update-password?email=${email}`),
	signIn: "/sign-in",
	accountName: {
		signIn: slug((accountName) => `/${accountName}/sign-in`),
		maps: {
			root: slug((accountName) => `/${accountName}/maps`),
			mapId: {
				root: slug((accountName, mapId) => `/${accountName}/maps/${mapId}`),
				tools: {
					root: slug(
						(accountName, mapId) => `/${accountName}/maps/${mapId}/tools`,
					),
					legend: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/legend`,
					),
					search: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/search`,
					),
					advancedSearch: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/advanced-search`,
					),
					query: slug(
						(accountName, mapId) => `/${accountName}/maps/${mapId}/tools/query`,
					),
					proximityAnalysis: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/proximity-analysis`,
					),
					informationSidebar: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/information-sidebar`,
					),
					print: slug(
						(accountName, mapId) => `/${accountName}/maps/${mapId}/tools/print`,
					),
					measure: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/measure`,
					),
					copyright: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/copyright`,
					),
					mouseHighlight: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/mouse-highlight`,
					),
					scaleBar: slug(
						(accountName, mapId) =>
							`/${accountName}/maps/${mapId}/tools/scale-bar`,
					),
				},
				settings: {
					root: slug(
						(accountName, mapId) => `/${accountName}/maps/${mapId}/settings`,
					),
				},
				mapName: {
					root: slug(
						(accountName, mapId, mapName) =>
							`/${accountName}/maps/${mapId}/${pret(mapName)}`,
					),
				},
			},
		},
		data: {
			root: slug((accountName) => `/${accountName}/data`),
			import: slug((accountName, mapId?: string, layerGroupId?: string) => {
				const baseUrl = `/${accountName}/data/import`;
				const params: string[] = [];

				if (mapId) params.push(`map-id=${mapId}`);
				if (layerGroupId) params.push(`layer-group-id=${layerGroupId}`);

				return params.length > 0 ? `${baseUrl}?${params.join("&")}` : baseUrl;
			}),
			datasetId: {
				root: slug(
					(accountName, datasetId) => `/${accountName}/data/${datasetId}`,
				),
				settings: slug(
					(accountName, datasetId) =>
						`/${accountName}/data/${datasetId}/settings`,
				),
				datasetName: {
					root: slug(
						(accountName, datasetId, datasetName) =>
							`/${accountName}/data/${datasetId}/${pret(datasetName)}`,
					),
				},
			},
		},
		users: {
			root: slug((accountName) => `/${accountName}/users`),
			invite: slug((accountName) => `/${accountName}/users/invite`),
			edit: slug(
				(accountName, userId) => `/${accountName}/users/${userId}/edit`,
			),
			join: slug(
				(accountName, userId) => `/${accountName}/users/${userId}/join`,
			),
			managedOwnerJoin: slug(
				(accountName, userId, reverseHandoff?: boolean) =>
					`/${accountName}/users/${userId}/owner-join${reverseHandoff ? "?reverse-handoff=true" : ""}`,
			),
		},
		groups: {
			root: slug((accountName) => `/${accountName}/groups`),
			create: slug((accountName) => `/${accountName}/groups/create`),
			edit: slug(
				(accountName, groupId) => `/${accountName}/groups/${groupId}/edit`,
			),
		},
		branding: {
			root: slug((accountName) => `/${accountName}/branding`),
			portal: slug((accountName) => `/${accountName}/branding/portal`),
			theme: slug((accountName) => `/${accountName}/branding/theme`),
			localisation: slug(
				(accountName) => `/${accountName}/branding/localisation`,
			),
			domainAndWhiteLabel: slug(
				(accountName) => `/${accountName}/branding/domain-and-white-label`,
			),
			analytics: slug((accountName) => `/${accountName}/branding/analytics`),
		},
		support: slug((accountName) => `/${accountName}/support`),
		settings: {
			root: slug((accountName) => `/${accountName}/settings`),
			checkout: slug((accountName) => `/${accountName}/settings/checkout`),
			cancelSubscription: slug(
				(accountName) => `/${accountName}/settings/cancel-subscription`,
			),
			editSubscription: slug(
				(accountName) => `/${accountName}/settings/edit-subscription`,
			),
			managedAccountRecovery: slug(
				(accountName) => `/${accountName}/settings/managed-account-recovery`,
			),
			subscriptionConfirmation: slug(
				(accountName) => `/${accountName}/settings/subscription-confirmation`,
			),
			invoice: slug((accountName) => `/${accountName}/settings/invoice`),
			profile: slug((accountName) => `/${accountName}/settings/profile`),
			password: slug((accountName) => `/${accountName}/settings/password`),
		},
	},
} as const;

// No longer stage-gated: /contact is served by Next on every stage now that it
// is in the always-on CloudFront allowlist (LB-1), so the prod mailto fallback
// is gone. The "V1Or2" name is historical — kept because six components import
// it. Requires GOOGLE_RECAPTCHA_SITE_KEY (v2 Invisible) on every stage, or the
// form renders a Google "Invalid site key" error.
export const contactV1Or2 = routes.contact;
export const partnersContactMailto = "mailto:sales@mangomap.com";
